// Captures and persists marketing attribution (UTM, fbclid, gclid, referrer,
// landing path) - tracking BOTH first-click (acquisition) and last-click
// (most recent campaign touch) - plus Meta Pixel's first-party cookies
// (_fbp, _fbc) so we can attribute leads to the originating ad.

export type TouchSnapshot = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  utm_campaign_id: string | null;
  utm_adset_id: string | null;
  utm_ad_id: string | null;
  landing_path: string | null;
  referrer: string | null;
  touch_at: string | null;
};

export type Attribution = TouchSnapshot & {
  first: TouchSnapshot;
  last: TouchSnapshot;
  fbp: string | null;
  fbc: string | null;
};

import {
  parseTrackingParams,
  hasTrackingParams,
  inferSourceFromReferrer,
  SERVER_ATTR_COOKIE,
} from "./tracking-params";

const STORAGE_KEY = "lead_attribution_v2";
const LEGACY_KEY = "lead_attribution_v1";
const COOKIE_KEY = "lead_attr_v2";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // 90 days

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function truncate(v: string | null | undefined, n: number): string | null {
  if (!v) return null;
  const s = v.trim();
  return s ? s.slice(0, n) : null;
}

function emptyTouch(): TouchSnapshot {
  return {
    utm_source: null, utm_medium: null, utm_campaign: null,
    utm_content: null, utm_term: null, fbclid: null, gclid: null,
    utm_campaign_id: null, utm_adset_id: null, utm_ad_id: null,
    landing_path: null, referrer: null, touch_at: null,
  };
}

type Stored = {
  first?: Partial<TouchSnapshot>;
  last?: Partial<TouchSnapshot>;
};

function readStored(): Stored {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
    // Migrate v1 (single touch) → v2 (first === last)
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const l = JSON.parse(legacy);
      return { first: l, last: l };
    }
    // localStorage can be empty because a privacy mode / ITP eviction wiped
    // it. Fall back to the first-party cookie mirror so an ad click's UTMs
    // are never lost between landing and submit.
    const cookie = readCookie(COOKIE_KEY);
    if (cookie) return JSON.parse(cookie);
    return {};
  } catch {
    return {};
  }
}

/**
 * Attribution the worker recorded on the landing request itself. This is the
 * safety net for browsers where JS storage is unavailable or wiped mid-session
 * (in-app Facebook/Instagram webviews, private modes, aggressive blockers):
 * the params were already banked in a first-party cookie before any script ran.
 */
function readServerTouch(): Partial<TouchSnapshot> | null {
  const raw = readCookie(SERVER_ATTR_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TouchSnapshot> & { t?: string };
    if (!parsed || typeof parsed !== "object") return null;
    return { ...parsed, touch_at: parsed.touch_at ?? parsed.t ?? null };
  } catch {
    return null;
  }
}

function writeStored(s: Stored) {
  if (typeof window === "undefined") return;
  const json = JSON.stringify(s);
  try {
    localStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* ignore */
  }
  try {
    // Mirror to a first-party cookie (survives localStorage eviction and is
    // readable when storage access is blocked entirely).
    if (json.length < 3500) {
      document.cookie = `${COOKIE_KEY}=${encodeURIComponent(json)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
    }
  } catch {
    /* ignore */
  }
}

function mergeTouch(base: Partial<TouchSnapshot> | undefined, fresh: Partial<TouchSnapshot>): TouchSnapshot {
  const e = emptyTouch();
  return {
    utm_source: base?.utm_source ?? fresh.utm_source ?? e.utm_source,
    utm_medium: base?.utm_medium ?? fresh.utm_medium ?? e.utm_medium,
    utm_campaign: base?.utm_campaign ?? fresh.utm_campaign ?? e.utm_campaign,
    utm_content: base?.utm_content ?? fresh.utm_content ?? e.utm_content,
    utm_term: base?.utm_term ?? fresh.utm_term ?? e.utm_term,
    fbclid: base?.fbclid ?? fresh.fbclid ?? e.fbclid,
    gclid: base?.gclid ?? fresh.gclid ?? e.gclid,
    utm_campaign_id: base?.utm_campaign_id ?? fresh.utm_campaign_id ?? e.utm_campaign_id,
    utm_adset_id: base?.utm_adset_id ?? fresh.utm_adset_id ?? e.utm_adset_id,
    utm_ad_id: base?.utm_ad_id ?? fresh.utm_ad_id ?? e.utm_ad_id,
    landing_path: base?.landing_path ?? fresh.landing_path ?? e.landing_path,
    referrer: base?.referrer ?? fresh.referrer ?? e.referrer,
    touch_at: base?.touch_at ?? fresh.touch_at ?? e.touch_at,
  };
}

/**
 * Capture attribution from the current URL + referrer.
 * - FIRST touch: locked on the user's very first visit; never overwritten.
 * - LAST touch: overwritten whenever a NEW campaign click is detected
 *   (fresh fbclid/gclid/utm_source/utm_campaign).
 */
export function captureAttribution(): Attribution {
  if (typeof window === "undefined") {
    const e = emptyTouch();
    return { ...e, first: e, last: e, fbp: null, fbc: null };
  }
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const stored = readStored();
  const now = new Date().toISOString();

  // Case-insensitive, alias-aware parse (adset_name, ad_id, capitalised keys,
  // bare click ids all resolve to the canonical fields).
  const incoming: Partial<TouchSnapshot> = { ...parseTrackingParams(params) };

  // The worker banked the landing params in a cookie before any JS ran; use it
  // to fill anything the URL no longer carries (e.g. a link that stripped the
  // query, or a client-side navigation away from the landing URL).
  const serverTouch = readServerTouch();
  if (serverTouch) {
    for (const k of [
      "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
      "fbclid", "gclid", "utm_campaign_id", "utm_adset_id", "utm_ad_id",
    ] as const) {
      if (!incoming[k] && serverTouch[k]) incoming[k] = truncate(serverTouch[k], 490);
    }
  }

  // ANY tracking param (not just source/campaign) marks a fresh campaign
  // click. A partial template such as "?utm_content=Hook 8" used to be
  // discarded entirely.
  const hasNewTouch = hasTrackingParams(params) || Boolean(serverTouch && !stored.last?.touch_at);

  // Last resort: no params anywhere, but the referrer names the platform.
  // Recorded as medium=referral/organic so paid reporting stays honest.
  if (!incoming.utm_source) {
    const inferred = inferSourceFromReferrer(document.referrer || null);
    if (inferred) {
      incoming.utm_source = inferred.utm_source;
      if (!incoming.utm_medium) incoming.utm_medium = inferred.utm_medium;
    }
  }

  // Prefer the landing URL the worker saw (it still carries the query string)
  // when the current URL has already been navigated past.
  const landingPath =
    truncate(url.search ? url.pathname + url.search : null, 990) ??
    truncate(serverTouch?.landing_path, 990) ??
    truncate(url.pathname, 990);
  const referrerValue =
    truncate(document.referrer || null, 990) ?? truncate(serverTouch?.referrer, 990);

  // FIRST: lock on initial visit. landing_path + referrer always captured.
  const first: TouchSnapshot = stored.first?.touch_at
    ? mergeTouch(stored.first, {})
    : {
        ...incoming,
        landing_path: landingPath,
        referrer: referrerValue,
        touch_at: now,
      } as TouchSnapshot;
  // Fill any missing fields
  const firstFinal = mergeTouch(first, emptyTouch());

  // LAST: overwrite on new campaign click; otherwise carry stored last (or fall back to first).
  let last: TouchSnapshot;
  if (hasNewTouch) {
    last = {
      ...emptyTouch(),
      ...incoming,
      landing_path: landingPath,
      referrer: referrerValue,
      touch_at: now,
    } as TouchSnapshot;
  } else {
    last = stored.last?.touch_at ? mergeTouch(stored.last, {}) : firstFinal;
  }

  writeStored({ first: firstFinal, last });

  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc && last.fbclid) {
    fbc = `fb.1.${Date.now()}.${last.fbclid}`;
  }

  // Top-level fields mirror LAST touch (backwards compatibility with existing
  // call sites that read attribution.utm_source etc.).
  return {
    ...last,
    first: firstFinal,
    last,
    fbp,
    fbc,
  };
}

export function getAttribution(): Attribution {
  // Re-capture every read so we always have the latest cookies + landing
  // info, even if a different tab loaded first.
  return captureAttribution();
}
